/**
 * RestoID — passkey sign-in (Claude Design method row).
 * ---------------------------------------------------------------------------
 * Custom RestoID PRESENTATION on the PRISTINE upstream passkey LOGIC: it reuses
 * the same WebAuthn context, `usePasskeySignIn` verification, conditional-UI
 * abort and error handling as `@/components/Button/PasskeySignInButton`, but
 * renders the design's method row (fingerprint tile + clear "Ключ доступа /
 * Face ID, Touch ID или ключ" label) instead of the misleading
 * "sign in with <webauthn>" wording.
 */
import { InteractionEvent } from '@logto/schemas';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { useCallback, useContext, useState } from 'react';
import { useDebouncedLoader } from 'use-debounced-loader';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import WebAuthnContext from '@/Providers/WebAuthnContextProvider/WebAuthnContext';
import usePasskeySignIn from '@/hooks/use-passkey-sign-in';
import useSubmitInteractionErrorHandler from '@/hooks/use-submit-interaction-error-handler';

import { useCopy } from '../copy';

import { C, FONT, Icon, Spin } from './restoid';

const PasskeySignIn = () => {
  const c = useCopy();
  const { isPreview } = useContext(PageContext);
  const {
    authenticationOptions,
    isLoading: isPreparing,
    isPasskeyFlowProcessing,
    setIsPasskeyFlowProcessing,
    markAuthenticationOptionsConsumed,
    abortConditionalUI,
  } = useContext(WebAuthnContext);
  const { handleVerifyPasskey } = usePasskeySignIn();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hover, setHover] = useState(false);
  const isLoadingActive = useDebouncedLoader(isSubmitting, 300);
  const isDisabled = isPreparing || !authenticationOptions || isPasskeyFlowProcessing;

  const preSignInErrorHandler = useSubmitInteractionErrorHandler(InteractionEvent.SignIn, {
    replace: true,
  });

  const onPasskeySignIn = useCallback(async () => {
    if (!authenticationOptions) {
      return;
    }
    // Abort any pending conditional-UI request before the manual flow to avoid
    // `OperationError: A request is already pending` (mirrors upstream).
    abortConditionalUI();
    setIsSubmitting(true);
    setIsPasskeyFlowProcessing(true);
    try {
      await handleVerifyPasskey(authenticationOptions, preSignInErrorHandler);
    } finally {
      markAuthenticationOptionsConsumed();
      setIsPasskeyFlowProcessing(false);
      setIsSubmitting(false);
    }
  }, [
    abortConditionalUI,
    authenticationOptions,
    handleVerifyPasskey,
    markAuthenticationOptionsConsumed,
    preSignInErrorHandler,
    setIsPasskeyFlowProcessing,
  ]);

  if (!browserSupportsWebAuthn()) {
    return null;
  }

  const off = isDisabled && !isPreview;

  return (
    <button
      type="button"
      disabled={off}
      onClick={() => void onPasskeySignIn()}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        height: 56,
        textAlign: 'left',
        padding: '0 10px',
        borderRadius: 12,
        cursor: off ? 'default' : 'pointer',
        opacity: off ? 0.55 : 1,
        background: hover && !off ? C.cardHover : C.card,
        border: `1px solid ${hover && !off ? C.line : C.lineSoft}`,
        transition: 'background .15s ease, border-color .15s ease, opacity .15s ease',
      }}
    >
      <span
        style={{
          flex: '0 0 auto',
          width: 36,
          height: 36,
          borderRadius: 9,
          background: C.tile,
          display: 'grid',
          placeItems: 'center',
          color: C.brand,
          border: `1px solid ${C.tileLine}`,
        }}
      >
        <Icon name="finger" size={18} />
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          textAlign: 'center',
          color: C.text,
          font: `600 14px/1.2 ${FONT}`,
        }}
      >
        {c('passkeyInline')}
      </span>
      {/* Symmetric to the 36px tile so the centred label is centred in the button
          (mirrors the Yandex button: icon + centred text + spinner slot). */}
      <span style={{ flex: '0 0 auto', display: 'grid', placeItems: 'center', width: 36 }}>
        {isLoadingActive ? <Spin size={16} bw={2} /> : null}
      </span>
    </button>
  );
};

export default PasskeySignIn;
